/**
 * OAuth2 State Parameter Utilities
 * 
 * Proper OAuth2 implementation using state parameter to carry application data
 * through the OAuth flow instead of including it in redirect URLs.
 */

/**
 * Creates and encodes OAuth state parameter
 * @param {Object} params - { redirectUrl, siteAppId, provider }
 * @returns {string} Base64 encoded state parameter
 */
export const createOAuthState = (params) => {
	const state = {
		...params,
		timestamp: Date.now()
	};
	return btoa(JSON.stringify(state));
};

/**
 * Decodes and validates OAuth state parameter
 * @param {string} encodedState - Base64 encoded state
 * @returns {Object|null} Decoded state object or null if invalid
 */
export const decodeOAuthState = (encodedState) => {
	try {
		if (!encodedState) return null;
		
		const state = JSON.parse(atob(encodedState));
		
		// Validate timestamp (max 1 hour for security)
		const maxAge = 60 * 60 * 1000; // 1 hour
		if (Date.now() - state.timestamp > maxAge) {
			console.warn('OAuth state expired');
			return null;
		}
		
		return state;
	} catch (error) {
		console.error('Invalid OAuth state:', error);
		return null;
	}
};

/**
 * Extracts current page parameters for OAuth state
 * @returns {Object} Parameters to include in OAuth state
 */
export const extractOAuthParams = () => {
	const urlParams = new URLSearchParams(window.location.search);
	
	return {
		redirectUrl: urlParams.get('redirectUrl') || window.location.pathname,
		siteAppId: urlParams.get('siteAppId'),
		timestamp: Date.now()
	};
};